import React, { useState, useEffect, useRef, useContext } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faPaperclip,
  faDownload,
  faRobot,
  faUser,
  faImage,
  faFile,
  faTimes,
  faUserCog, // Changed from faUserTie to faUserCog (available icon)
  faBrain,
  faPencilAlt,
  faSearch,
  faBullseye,
  faHashtag,
  faHandshake,
  faTrophy,
  faQuestionCircle, // Added for user questions
  faCode,
  faCopy,
  faCodeBranch,
  faChevronDown,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons';
import { SettingsContext } from '../contexts/SettingsContext';
import { SupervisorEngine } from '../services/supervisor-engine';
import LogViewer from './LogViewer';
import FileUploadModal from './FileUploadModal';
import FileViewer from './FileViewer';
import { uploadFile } from '../utils/storage';
import conversationService from '../services/conversationService';
import { DeliverableCollector } from '../services/deliverable-collector';
import FinalSummaryView from './FinalSummaryView';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: rgba(255, 255, 255, 0.03);
  position: relative;
  font-family: var(--font-family);
  font-weight: 400;
  backdrop-filter: blur(1px);
  -webkit-backdrop-filter: blur(1px);
`;

const ChatHeader = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: var(--space-6) var(--space-8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-5);
`;

const CharacterIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: var(--radius-full);
  background: var(--primary-600);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: var(--text-base);
  font-weight: 600;
  box-shadow: var(--shadow-md);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &:hover {
    transform: translateY(-2px) scale(1.08);
    box-shadow: var(--shadow-lg);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }
`;

const CharacterInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const CharacterName = styled.div`
  font-weight: 500;
  font-size: var(--text-lg);
  color: var(--text-color);
  line-height: 1.4;
  letter-spacing: -0.01em;
  font-family: var(--font-heading-bold);
`;

const CharacterRole = styled.div`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  font-weight: 300;
  line-height: 1.4;
  opacity: 0.8;
  font-family: var(--font-heading-regular);
`;

const HeaderActions = styled.div`
  display: flex;
  gap: var(--space-2);
  align-items: center;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-lg);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: var(--text-base);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;

  &:hover {
    background: rgba(74, 144, 226, 0.1);
    color: rgba(74, 144, 226, 0.8);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
    background: rgba(74, 144, 226, 0.15);
  }

  &:focus-visible {
    outline: 2px solid rgba(74, 144, 226, 0.6);
    outline-offset: 2px;
  }

  .dark-mode & {
    &:hover {
      background: rgba(187, 134, 252, 0.1);
      color: rgba(187, 134, 252, 0.8);
    }
    &:active {
      background: rgba(187, 134, 252, 0.2);
      color: rgba(187, 134, 252, 0.9);
    }
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: var(--space-8) var(--space-6);
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  scroll-behavior: smooth;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  position: relative;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: var(--radius-full);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }

  .dark-mode & {
    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
    }
    &::-webkit-scrollbar-thumb:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  justify-content: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  align-items: flex-start;
  gap: 0.75rem;
  max-width: 100%;
`;

const MessageBubble = styled.div`
  max-width: 75%;
  padding: var(--space-4) var(--space-6);
  background: ${props => props.isUser ?
    'rgba(74, 144, 226, 0.8)' :
    'rgba(255, 255, 255, 0.1)'
  };
  color: ${props => props.isUser ? 'white' : 'var(--text-color)'};
  border-radius: ${props => props.isUser ?
    'var(--radius-2xl) var(--radius-2xl) var(--radius-lg) var(--radius-2xl)' :
    'var(--radius-2xl) var(--radius-2xl) var(--radius-2xl) var(--radius-lg)'
  };
  box-shadow: ${props => props.isUser ?
    '0 12px 30px rgba(74, 144, 226, 0.4)' :
    '0 2px 10px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.2)'
  };
  position: relative;
  line-height: 1.7;
  font-weight: 400;
  font-size: var(--text-base);
  word-wrap: break-word;
  border: ${props => props.isUser ? 'none' : '1px solid rgba(255, 255, 255, 0.1)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: ${props => props.isUser ? 'blur(8px)' : 'blur(12px)'};
  -webkit-backdrop-filter: ${props => props.isUser ? 'blur(8px)' : 'blur(12px)'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.isUser ?
      '0 16px 40px rgba(74, 144, 226, 0.5)' :
      '0 4px 15px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.3)'
    };
  }

  .dark-mode & {
    background: ${props => props.isUser ?
      'rgba(187, 134, 252, 0.9)' :
      'rgba(30, 30, 30, 0.8)'
    };
    border-color: ${props => props.isUser ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
    box-shadow: ${props => props.isUser ?
      '0 8px 25px rgba(187, 134, 252, 0.3)' :
      '0 4px 15px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.2)'
    };
  }
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.isUser ? 'rgba(255, 255, 255, 0.8)' : 'var(--text-secondary)'};
`;

const MessageContent = styled.div`
  line-height: 1.6;
  word-wrap: break-word;
  font-weight: 400;
  min-height: auto;
  white-space: pre-wrap;

  h1, h2, h3, h4, h5, h6 {
    font-weight: 600;
    line-height: 1.4;
    margin: 1rem 0 0.5rem;
  }

  h1 { font-size: 1.5rem; }
  h2 { font-size: 1.375rem; }
  h3 { font-size: 1.25rem; }
  h4 { font-size: 1.125rem; }
  h5 { font-size: 1rem; }
  h6 { font-size: 0.875rem; }

  p {
    margin: 0.75rem 0;
  }

  ul, ol {
    margin: 0.75rem 0;
    padding-right: 1.5rem;
  }

  li {
    margin: 0.25rem 0;
  }

  strong, b {
    font-weight: 600;
  }

  code {
    background: #f3f4f6;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.875rem;
    font-weight: 500;
  }

  pre {
    background: #f3f4f6;
    padding: 1rem;
    border-radius: 8px;
    overflow-x: auto;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.875rem;
    line-height: 1.4;
  }

  /* Ensure content takes full height */
  display: block;
  width: 100%;
`;

const AgentCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-right: 5px solid ${props => props.borderColor};
  margin-bottom: 1rem;
  transition: all 0.3s ease;
  position: relative;
  min-height: auto;

  &.active {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    border-right-color: ${props => props.borderColor};
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }
`;

const AgentHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  gap: 0.75rem;
`;

const AgentIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 24px;
  background: ${props => props.bgColor}40;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  margin-left: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const AgentInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AgentName = styled.div`
  font-weight: 600;
  color: var(--text-color);
  font-size: 1rem;
  line-height: 1.2;
`;

const AgentRole = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 400;
  line-height: 1.2;
`;

const FileAttachment = styled.div`
  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  margin: 0.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const FileAttachments = styled.div`
  padding: 0.5rem 1.5rem;
  background: var(--surface-color);
  border-top: 1px solid var(--border-color);
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const ToolResultCard = styled.div`
  background: var(--surface-warm);
  border: 1px solid var(--border-warm);
  border-radius: 12px;
  padding: 1rem;
  margin: 0.5rem 0;
  border-right: 4px solid var(--accent-color);
`;

const ToolResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--accent-color);
`;

const InputContainer = styled.div`
  padding: var(--space-4) var(--space-6);
  background: transparent;
  border: none;
  display: flex;
  gap: var(--space-3);
  align-items: flex-end;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  box-shadow: none;
  border-radius: 0;
`;

const InputWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const MessageInput = styled.textarea`
  width: 100%;
  min-height: 48px;
  max-height: 120px;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border-color-light);
  border-radius: var(--radius-xl);
  font-size: var(--text-base);
  font-family: var(--font-family);
  font-weight: 400;
  font-weight: 400;
  resize: vertical;
  direction: rtl;
  background: rgba(255, 255, 255, 0.2);
  color: var(--text-color);
  line-height: 1.6;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:focus {
    outline: none;
    border-color: rgba(74, 144, 226, 0.4);
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: rgba(74, 144, 226, 0.3);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.25);
  }

  &::placeholder {
    color: var(--text-secondary);
    font-weight: 400;
    opacity: 0.7;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: var(--radius-full);
  }

  .dark-mode & {
    background: var(--neutral-200);
    border-color: var(--neutral-300);
    &:focus {
      box-shadow: 0 0 0 3px var(--primary-900), var(--shadow-md);
    }
    &:hover:not(:focus) {
      border-color: var(--primary-700);
    }
  }
`;

const SendButton = styled.button`
  width: 44px;
  height: 44px;
  background: rgba(74, 144, 226, 0.8);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-lg);
  font-family: var(--font-heading-regular);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 12px rgba(74, 144, 226, 0.3);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    border-radius: inherit;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover:not(:disabled) {
    background: rgba(74, 144, 226, 0.9);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(74, 144, 226, 0.4);
    border-color: rgba(255, 255, 255, 0.5);

    &::before {
      opacity: 0.5;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(74, 144, 226, 0.3);
    transition: all 0.1s ease;
  }

  &:focus-visible {
    outline: 2px solid rgba(74, 144, 226, 0.8);
    outline-offset: 2px;
  }

  &:disabled {
    background: rgba(128, 128, 128, 0.3);
    color: rgba(128, 128, 128, 0.6);
    cursor: not-allowed;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    transform: none;
    border-color: rgba(128, 128, 128, 0.2);

    &::before {
      display: none;
    }
  }

  /* Pulse effect when there's text */
  ${props => props.hasText && `
    box-shadow: 0 2px 12px rgba(74, 144, 226, 0.4), 0 0 0 1px rgba(74, 144, 226, 0.3);
    animation: subtlePulse 2s ease-in-out infinite;

    @keyframes subtlePulse {
      0%, 100% {
        box-shadow: 0 2px 12px rgba(74, 144, 226, 0.4), 0 0 0 1px rgba(74, 144, 226, 0.3);
      }
      50% {
        box-shadow: 0 4px 16px rgba(74, 144, 226, 0.5), 0 0 0 2px rgba(74, 144, 226, 0.4);
      }
    }
  `}

  .dark-mode & {
    background: rgba(187, 134, 252, 0.8);
    box-shadow: 0 2px 12px rgba(187, 134, 252, 0.3);

    &:hover:not(:disabled) {
      background: rgba(187, 134, 252, 0.9);
      box-shadow: 0 4px 16px rgba(187, 134, 252, 0.4);
      border-color: rgba(255, 255, 255, 0.4);
    }

    &:disabled {
      background: rgba(64, 64, 64, 0.4);
      color: rgba(128, 128, 128, 0.5);
      border-color: rgba(64, 64, 64, 0.3);
    }

    ${props => props.hasText && `
      box-shadow: 0 2px 12px rgba(187, 134, 252, 0.4), 0 0 0 1px rgba(187, 134, 252, 0.3);

      @keyframes subtlePulse {
        0%, 100% {
          box-shadow: 0 2px 12px rgba(187, 134, 252, 0.4), 0 0 0 1px rgba(187, 134, 252, 0.3);
        }
        50% {
          box-shadow: 0 4px 16px rgba(187, 134, 252, 0.5), 0 0 0 2px rgba(187, 134, 252, 0.4);
        }
      }
    `}
  }
`;

const FileButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-lg);

  &:hover {
    background: rgba(74, 144, 226, 0.1);
    color: rgba(74, 144, 226, 0.8);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(74, 144, 226, 0.6);
    outline-offset: 2px;
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2.5rem;
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 0.9rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: radial-gradient(circle, var(--warm-hover) 0%, transparent 70%);
    transform: translate(-50%, -50%);
    animation: expand 2s infinite;
  }

  .loader {
    width: 28px;
    height: 28px;
    border: 3px solid var(--border-color-light);
    border-top: 3px solid var(--gradient-primary);
    border-radius: 50%;
    animation: spin 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    position: relative;
    z-index: 1;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
      filter: drop-shadow(0 0 5px rgba(255, 109, 0, 0.3));
    }
    50% {
      filter: drop-shadow(0 0 15px rgba(255, 109, 0, 0.6));
    }
    100% {
      transform: rotate(360deg);
      filter: drop-shadow(0 0 5px rgba(255, 109, 0, 0.3));
    }
  }

  @keyframes expand {
    0%, 100% {
      width: 0;
      height: 0;
      opacity: 0.8;
    }
    50% {
      width: 80px;
      height: 80px;
      opacity: 0.4;
    }
  }
`;

const ThinkingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-secondary);
  margin: 0.75rem 0;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  background: var(--surface-warm);
  border-radius: 12px;
  border: 1px solid var(--border-warm);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 109, 0, 0.05) 50%, transparent 100%);
    animation: shimmer 2s infinite;
  }

  .loader {
    width: 18px;
    height: 18px;
    border: 2px solid var(--border-color-light);
    border-top: 2px solid var(--accent-color);
    border-radius: 50%;
    animation: spin 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    position: relative;
    z-index: 1;
    filter: drop-shadow(0 0 8px rgba(255, 109, 0, 0.3));
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
      filter: drop-shadow(0 0 4px rgba(255, 109, 0, 0.3));
    }
    50% {
      filter: drop-shadow(0 0 12px rgba(255, 109, 0, 0.6));
    }
    100% {
      transform: rotate(360deg);
      filter: drop-shadow(0 0 4px rgba(255, 109, 0, 0.3));
    }
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
`;

// Premium user question card with warm colors
const UserQuestionCard = styled.div`
  background: var(--gradient-secondary);
  color: white;
  padding: 1.5rem 1.75rem;
  border-radius: 20px;
  margin: 1.25rem 0;
  box-shadow: 0 4px 16px var(--shadow-3), 0 0 20px rgba(123, 31, 162, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  animation: float 3s ease-in-out infinite;

  /* Multi-layer gradient overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, transparent 30%),
      radial-gradient(circle at 20% 80%, rgba(255, 109, 0, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  /* Animated border */
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: var(--gradient-accent);
    border-radius: 22px;
    z-index: -1;
    animation: shimmer 3s infinite;
    opacity: 0.6;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-2px); }
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin-bottom: 1.25rem;
  font-weight: 700;
  font-size: 1.1rem;
  position: relative;
  z-index: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.6; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.2); }
  }
`;

const QuestionText = styled.div`
  font-size: 1.05rem;
  line-height: 1.6;
  margin-bottom: 1.25rem;
  font-weight: 500;
  position: relative;
  z-index: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.2px;
`;

const QuestionPrompt = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  opacity: 0.8;
  margin-top: 0.5rem;
  color: #6b7280;
`;

const ScrollToBottomButton = styled.button`
  position: fixed;
  bottom: 120px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(74, 144, 226, 0.9);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  padding: var(--space-3) var(--space-5);
  font-size: var(--text-sm);
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  animation: slideUp 0.3s ease-out;

  &:hover {
    background: rgba(74, 144, 226, 1);
    transform: translateX(-50%) translateY(-2px);
    box-shadow: 0 6px 16px rgba(74, 144, 226, 0.4);
  }

  &:active {
    transform: translateX(-50%) translateY(0);
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  .dark-mode & {
    background: rgba(187, 134, 252, 0.9);
    box-shadow: 0 4px 12px rgba(187, 134, 252, 0.3);

    &:hover {
      background: rgba(187, 134, 252, 1);
      box-shadow: 0 6px 16px rgba(187, 134, 252, 0.4);
    }
  }
`;

function ChatWindowEnhanced({ userId, onFileUpload, conversationId }) {
  console.log('[ChatWindowEnhanced] Component rendered with props:', { userId, conversationId });
  console.log('[ChatWindowEnhanced] conversationId type:', typeof conversationId, 'value:', conversationId);
  const {
    settings,
    currentCharacter,
    currentConversation,
    setCurrentConversation,
    characters,
    supervisorState,
    setSupervisorState,
    textDirection
  } = useContext(SettingsContext);

  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [supervisorEngine, setSupervisorEngine] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [streamingMessage, setStreamingMessage] = useState(null); // For streaming AI responses
  const [isFileUploadOpen, setIsFileUploadOpen] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true); // Track if auto-scroll is enabled
  const [showScrollButton, setShowScrollButton] = useState(false); // Show scroll to bottom button
  const [userHasScrolled, setUserHasScrolled] = useState(false); // Track if user manually scrolled
  const [deliverables, setDeliverables] = useState(null); // Collected deliverables
  const [showFinalSummary, setShowFinalSummary] = useState(false); // Show final summary

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Check if current character is supervisor (Itai Cohen)
  const isSupervisorMode = currentCharacter?.id === 'supervisor';

  // Initialize conversation service when userId is available
  useEffect(() => {
    if (userId) {
      conversationService.initialize(userId).catch(error => {
        console.error('Failed to initialize conversation service:', error);
      });
    }
  }, [userId]);

  // Load conversation when conversationId changes
  useEffect(() => {
    const loadConversation = async () => {
      console.log('[ChatWindowEnhanced] useEffect triggered - conversationId changed:', conversationId);
      if (conversationId && userId) {
        try {
          console.log('[ChatWindowEnhanced] Loading conversations from service...');
          const conversations = await conversationService.loadConversations();
          console.log('[ChatWindowEnhanced] Found', conversations.length, 'total conversations');

          const conversation = conversations.find(c => c.id === conversationId);
          if (conversation) {
            console.log('[ChatWindowEnhanced] Found matching conversation:', conversation);
            console.log('[ChatWindowEnhanced] Conversation has', conversation.messages?.length || 0, 'messages');
            console.log('[ChatWindowEnhanced] Calling setCurrentConversation...');
            setCurrentConversation(conversation);
            console.log('[ChatWindowEnhanced] setCurrentConversation called successfully');
          } else {
            console.warn('[ChatWindowEnhanced] Conversation not found for id:', conversationId);
            console.log('[ChatWindowEnhanced] Available conversation IDs:', conversations.map(c => c.id));
          }
        } catch (error) {
          console.error('[ChatWindowEnhanced] Error loading conversation:', error);
        }
      } else if (!conversationId) {
        console.log('[ChatWindowEnhanced] No conversationId, clearing current conversation');
        setCurrentConversation(null);
      }
    };
    loadConversation();
  }, [conversationId, userId, setCurrentConversation]);

  // Auto-scroll to bottom when new messages arrive (only if auto-scroll is enabled)
  useEffect(() => {
    // Only auto-scroll if user hasn't manually scrolled
    if (autoScroll && !userHasScrolled && messagesEndRef.current) {
      // Use a small delay to ensure DOM is updated
      const timeoutId = setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [
    currentConversation?.messages?.length,
    supervisorState.conversationHistory?.length,
    streamingMessage?.content,
    autoScroll,
    userHasScrolled
  ]);

  // Handle scroll events to detect user interaction
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let scrollTimeout;
    const handleScroll = () => {
      // Clear existing timeout
      clearTimeout(scrollTimeout);

      // Debounce scroll handling
      scrollTimeout = setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = container;
        // Dynamic threshold based on container height (5% of viewport)
        const threshold = Math.max(50, clientHeight * 0.05);
        const isAtBottom = scrollHeight - scrollTop - clientHeight < threshold;

        // If user scrolled to bottom, re-enable auto-scroll
        if (isAtBottom) {
          setAutoScroll(true);
          setShowScrollButton(false);
          setUserHasScrolled(false);
        } else {
          // User scrolled up, disable auto-scroll and show button
          setAutoScroll(false);
          setShowScrollButton(true);
          setUserHasScrolled(true);
        }
      }, 150); // Debounce for 150ms
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check for scroll position
    handleScroll();

    return () => {
      clearTimeout(scrollTimeout);
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Function to manually scroll to bottom
  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      // Use scrollTo for more reliable scrolling
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
    // Also scroll the ref as fallback
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    setAutoScroll(true);
    setShowScrollButton(false);
    setUserHasScrolled(false);
  };

  useEffect(() => {
    if (isSupervisorMode && !supervisorEngine) {
      const engine = new SupervisorEngine(
        characters,
        settings,
        (state) => {
          setSupervisorState(state);
        }
      );
      setSupervisorEngine(engine);
    }
  }, [isSupervisorMode, characters, settings, setSupervisorState]);

  // Create a flag to track if conversation was created for current supervisor session
  const [conversationCreatedForSession, setConversationCreatedForSession] = useState(false);

  // Reset flag when supervisor session starts
  useEffect(() => {
    if (supervisorState.isRunning && !conversationCreatedForSession) {
      setConversationCreatedForSession(false);
    }
  }, [supervisorState.isRunning]);

  // Collect deliverables when conversation history changes
  useEffect(() => {
    if (isSupervisorMode && supervisorState.conversationHistory) {
      const collected = DeliverableCollector.collect(supervisorState.conversationHistory);
      setDeliverables(collected);

      // Check if we have a system analysis with a title
      const analysisEntry = supervisorState.conversationHistory.find(
        entry => entry.type === 'system_analysis' && entry.conversationTitle
      );

      // Create conversation only ONCE when we have a title and haven't created one yet
      if (analysisEntry && !currentConversation && !conversationCreatedForSession && supervisorState.isRunning) {
        const createConversationWithTitle = async () => {
          try {
            // Collect ALL messages from supervisor history
            const allMessages = supervisorState.conversationHistory
              .filter(entry => entry.type === 'user_brief' || entry.type === 'user_response' || entry.type === 'agent_action')
              .map(entry => ({
                role: entry.type.includes('user') ? 'user' : 'assistant',
                content: entry.content,
                timestamp: entry.timestamp || new Date().toISOString(),
                agent: entry.agent,
                agentName: entry.agentName
              }));

            const newConv = await conversationService.saveConversation({
              id: `conv_${Date.now()}`,
              title: analysisEntry.conversationTitle,
              characterId: currentCharacter?.id,
              messages: allMessages,
              createdAt: Date.now(),
              updatedAt: Date.now(),
              supervisorHistory: supervisorState.conversationHistory // Save full history
            });

            setCurrentConversation(newConv);
            setConversationCreatedForSession(true); // Mark that we created a conversation
            console.log('[ChatWindowEnhanced] Created conversation with title:', analysisEntry.conversationTitle);
          } catch (error) {
            console.error('[ChatWindowEnhanced] Failed to create conversation:', error);
          }
        };

        createConversationWithTitle();
      } else if (currentConversation && supervisorState.conversationHistory.length > 0) {
        // Update existing conversation with new messages
        const updateConversation = async () => {
          try {
            const updatedConv = {
              ...currentConversation,
              supervisorHistory: supervisorState.conversationHistory,
              updatedAt: Date.now()
            };
            await conversationService.saveConversation(updatedConv);
          } catch (error) {
            console.error('[ChatWindowEnhanced] Failed to update conversation:', error);
          }
        };

        updateConversation();
      }

      // Check if project is completed
      const hasCompletion = supervisorState.conversationHistory.some(
        entry => entry.type === 'completion'
      );

      // Auto-show summary when project completes
      if (hasCompletion && collected.raw.length > 0) {
        setShowFinalSummary(true);
      }
    }
  }, [supervisorState.conversationHistory, isSupervisorMode, currentConversation, currentCharacter, conversationService]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    if (isLoading) return;

    // Save the message content before clearing
    const messageContent = message;

    // Clear the input field immediately
    setMessage('');

    // Handle supervisor mode (when chatting with Itai Cohen)
    if (isSupervisorMode) {
      if (supervisorState.waitingForUser && supervisorEngine) {
        // User is responding to supervisor question
        await supervisorEngine.submitUserResponse(messageContent);
        return;
      } else if (supervisorEngine && !supervisorState.isRunning) {
        // Start new supervisor session
        await supervisorEngine.start(messageContent);
        return;
      }
    }

    // Regular chat message
    const userMessage = {
      role: 'user',
      content: messageContent,
      timestamp: new Date().toISOString()
    };

    // Optimistic UI update
    setCurrentConversation(prev => ({
      ...prev,
      messages: [...(prev?.messages || []), userMessage]
    }));

    // Save to persistent storage
    try {
      if (currentConversation?.id) {
        await conversationService.addMessage(currentConversation.id, userMessage);
      } else {
        // Create new conversation if needed
        // Generate title from first message (max 50 chars)
        const messagePreview = userMessage.content.slice(0, 50).trim();
        const title = messagePreview.length < userMessage.content.length
          ? `${messagePreview}...`
          : messagePreview;

        const newConv = await conversationService.saveConversation({
          id: `conv_${Date.now()}`,
          title: title,
          characterId: currentCharacter?.id,
          messages: [userMessage],
          createdAt: Date.now(),
          updatedAt: Date.now()
        });
        setCurrentConversation(newConv);
      }
    } catch (error) {
      console.error('Failed to save user message:', error);
      // Continue anyway - message is in localStorage
    }

    setIsLoading(true);

    try {
      // Get API keys from localStorage
      const geminiApiKey = localStorage.getItem('geminiApiKey') || '';
      const anthropicApiKey = localStorage.getItem('anthropicApiKey') || '';
      
      const currentSettings = {
        system: currentCharacter?.systemPrompt || settings.systemPrompt,
        maxTokens: currentCharacter?.maxTokens || settings.maxTokens,
        temperature: currentCharacter?.temperature || settings.temperature,
        model: currentCharacter?.model || settings.model,
        aiProvider: settings.aiProvider || 'gemini'
      };

      // Get current character's tools
      const characterTools = currentCharacter?.tools || [];

      // Check if thinking mode should be enabled
      const enableThinking = currentCharacter?.thinkingMode && settings.aiProvider === 'gemini';
      const thinkingBudget = enableThinking ? (currentCharacter?.maxTokens || 8192) * 0.3 : 0; // 30% of max tokens

      // Prepare attachments: persist originals; when enhanced processing is ON, also generate and store .md, and send only Markdown content to server
      const preparedFiles = [];
      if (attachedFiles.length > 0) {
        for (let i = 0; i < attachedFiles.length; i++) {
          const a = attachedFiles[i];
          try {
            // Co-locate by group id
            const base = `${Date.now()}_${i}_${a.name}`;
            let groupId = '';
            try { groupId = btoa(unescape(encodeURIComponent(base))).replace(/=+$/,''); } catch { groupId = `${Date.now()}_${i}`; }

            // Upload original to Storage
            let originalUpload = null;
            if (a.file) {
              try {
                originalUpload = await uploadFile(userId, a.file, { conversationId: currentConversation?.id || null, fileGroupId: groupId });
              } catch (_) {}
            }

            if (settings?.enhancedDocProcessing !== false) {
              // Convert via /api/convert using the Storage URL for non-text; for text use inline
              let markdown = '';
              if ((a.type || '').startsWith('text/') && a.content) {
                markdown = String(a.content);
              } else if (originalUpload && originalUpload.downloadURL) {
                try {
                  const resp = await fetch('/api/convert', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ url: originalUpload.downloadURL, filename: a.name, mime: a.type })
                  });
                  if (resp.ok) {
                    const data = await resp.json();
                    if (data.success && data.markdown) markdown = data.markdown;
                  }
                } catch (_) {}
              }

              if (markdown) {
                const mdBlob = new Blob([markdown], { type: 'text/markdown' });
                const mdFile = new File([mdBlob], 'converted.md', { type: 'text/markdown' });
                try { await uploadFile(userId, mdFile, { conversationId: currentConversation?.id || null, fileGroupId: groupId, overrideName: 'converted.md' }); } catch {}
                preparedFiles.push({ name: 'converted.md', type: 'text/markdown', size: markdown.length, content: markdown });
              } else if (originalUpload && originalUpload.downloadURL) {
                // As a fallback, send URL to let the server convert if it can
                preparedFiles.push({ name: a.name, type: a.type, size: a.size, downloadURL: originalUpload.downloadURL });
              }
            } else {
              // Toggle OFF: do not convert; use original behavior
              preparedFiles.push({ name: a.name, type: a.type, size: a.size, content: a.content || '' });
            }
          } catch (e) {
            // If anything fails, fall back to previous behavior for this file
            preparedFiles.push({ name: a.name, type: a.type, size: a.size, content: a.content || '' });
          }
        }
      }

      // Enable streaming for real-time response
      const requestData = {
        messages: [...(currentConversation?.messages || []), userMessage],
        ...currentSettings,
        geminiApiKey,
        anthropicApiKey,
        tools: characterTools,
        enhancedDocProcessing: settings?.enhancedDocProcessing !== false,
        useThinking: enableThinking,
        thinkingBudget: thinkingBudget,
        stream: true,
        files: preparedFiles
      };
      
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let isFirstChunk = true;

      // Add initial streaming message
      setStreamingMessage({ content: '', done: false });

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              break;
            }

            try {
              const parsed = JSON.parse(data);
              if (parsed.content && parsed.content[0]?.text) {
                const chunkText = parsed.content[0].text;
                fullContent += chunkText;

                // Update streaming message in real-time
                setStreamingMessage({
                  content: fullContent,
                  done: false,
                  tools: parsed.tools
                });
              }
            } catch (e) {
              // Skip invalid JSON chunks
              console.debug('Skipping invalid JSON chunk:', data);
            }
          }
        }
      }

      // Final chunk to indicate completion
      setStreamingMessage({
        content: fullContent,
        done: true
      });

      // Add final message to conversation
      const assistantMessage = {
        role: 'assistant',
        content: fullContent,
        timestamp: new Date().toISOString(),
        tools: null // Will be set by the streaming response if available
      };

      // Update UI
      setCurrentConversation(prev => ({
        ...prev,
        messages: [...prev.messages, assistantMessage]
      }));

      // Save assistant message to persistent storage
      try {
        if (currentConversation?.id) {
          await conversationService.addMessage(currentConversation.id, assistantMessage);
        }
      } catch (error) {
        console.error('Failed to save assistant message:', error);
        // Continue anyway - message is in localStorage
      }

      // Clear streaming message and attached files
      setStreamingMessage(null);
      setAttachedFiles([]);
      
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = {
        role: 'system',
        content: `Error: ${error.message}. Please check your API key in settings.`,
        timestamp: new Date().toISOString()
      };
      
      setCurrentConversation(prev => ({
        ...prev,
        messages: [...(prev?.messages || []), errorMessage]
      }));

      // Save error message to persistent storage
      try {
        if (currentConversation?.id) {
          await conversationService.addMessage(currentConversation.id, errorMessage);
        }
      } catch (saveError) {
        console.error('Failed to save error message:', saveError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);

    for (const file of files) {
      // Check file size (limit to 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }

      let fileContent = '';

      // Read text files
      if (file.type.startsWith('text/')) {
        try {
          fileContent = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.readAsText(file);
          });
        } catch (error) {
          console.error('Error reading file:', error);
        }
      }

      setAttachedFiles(prev => [...prev, {
        name: file.name,
        type: file.type,
        size: file.size,
        content: fileContent,
        file: file // Keep the original file object
      }]);
    }

    // Reset file input
    event.target.value = '';
  };

  const removeAttachedFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const escapeHtml = (unsafe) => {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const formatContent = (content) => {
    if (!content) return '';

    const escapedContent = escapeHtml(content);

    return escapedContent
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/### (.*?)(?:\n|$)/g, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/## (.*?)(?:\n|$)/g, '<h2 class="text-xl font-bold mt-6 mb-3">$1</h2>')
      .replace(/^- (.*?)(?:\n|$)/gm, '<li class="ml-5 list-disc">$1</li>')
      .replace(/(<li.*<\/li>)+/g, '<ul class="mr-5">$&</ul>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>')
      .replace(/^/, '<p>')
      .replace(/$/, '</p>');
  };

  const renderToolResults = (tools) => {
    if (!tools || (!tools.results && !tools.fileAnalyses)) return null;

    return (
      <div>
        {tools.results && Object.keys(tools.results).length > 0 && (
          <div>
            <h4 style={{ color: 'var(--accent-color)', marginBottom: '0.5rem' }}>🔧 Tools Used:</h4>
            {tools.results.google_search && (
              <ToolResultCard>
                <ToolResultHeader>
                  <FontAwesomeIcon icon={faSearch} />
                  Google Search Results
                </ToolResultHeader>
                <div>
                  <strong>Query:</strong> {tools.results.google_search.query}
                  {tools.results.google_search.totalResults && (
                    <span style={{ marginLeft: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      ({tools.results.google_search.totalResults.toLocaleString()} total results, {tools.results.google_search.searchTime}s)
                    </span>
                  )}
                  <br />
                  <strong>Top Results:</strong>
                  <ul style={{ marginTop: '0.5rem' }}>
                    {tools.results.google_search.results.map((result, index) => (
                      <li key={index} style={{ marginBottom: '1rem' }}>
                        <div>
                          <a href={result.link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)', textDecoration: 'none', fontWeight: '600' }}>
                            {result.title}
                          </a>
                          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0.25rem 0' }}>
                            {result.displayLink}
                          </div>
                          <div style={{ fontSize: '0.875rem', lineHeight: '1.4' }}>{result.snippet}</div>
                          {result.relevance && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--accent-color)', marginTop: '0.25rem' }}>
                              Relevance: {result.relevance}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </ToolResultCard>
            )}

            {tools.results.code_execution && (
              <ToolResultCard>
                <ToolResultHeader>
                  <FontAwesomeIcon icon={faCode} />
                  Code Execution ({tools.results.code_execution.language})
                </ToolResultHeader>
                <div>
                  {tools.results.code_execution.error ? (
                    <div style={{ color: '#ef4444', background: '#fef2f2', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' }}>
                      <strong>Error:</strong> {tools.results.code_execution.result}
                    </div>
                  ) : (
                    <>
                      <strong>Source Code:</strong>
                      <pre style={{
                        background: 'var(--background-color)',
                        padding: '1rem',
                        borderRadius: '8px',
                        margin: '0.5rem 0',
                        fontSize: '0.875rem',
                        overflowX: 'auto',
                        border: '1px solid var(--border-color-light)',
                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                        lineHeight: '1.4'
                      }}>
                        {tools.results.code_execution.code}
                      </pre>
                      <strong>Execution Result:</strong>
                      <div style={{
                        background: '#f8fafc',
                        border: '1px solid var(--border-color-light)',
                        padding: '1rem',
                        borderRadius: '8px',
                        margin: '0.5rem 0',
                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        lineHeight: '1.4'
                      }}>
                        {tools.results.code_execution.result}
                      </div>
                    </>
                  )}
                  {tools.results.code_execution.timestamp && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                      Executed at: {new Date(tools.results.code_execution.timestamp).toLocaleString()}
                    </div>
                  )}
                </div>
              </ToolResultCard>
            )}

            {tools.results.image_generation && (
              <ToolResultCard>
                <ToolResultHeader>
                  <FontAwesomeIcon icon={faImage} />
                  Image Generation
                </ToolResultHeader>
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Original Prompt:</strong>
                    <div style={{ background: 'var(--background-color)', padding: '0.75rem', borderRadius: '4px', margin: '0.5rem 0', fontStyle: 'italic' }}>
                      {tools.results.image_generation.originalPrompt}
                    </div>
                  </div>

                  {tools.results.image_generation.result.success && tools.results.image_generation.result.imageData ? (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Generated Image:</strong>
                      <div style={{
                        background: 'var(--background-color)',
                        padding: '1rem',
                        borderRadius: '8px',
                        margin: '0.5rem 0',
                        textAlign: 'center',
                        border: '1px solid var(--border-color-light)'
                      }}>
                        <img
                          src={`data:${tools.results.image_generation.result.mimeType};base64,${tools.results.image_generation.result.imageData}`}
                          alt="Generated image"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '400px',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px var(--shadow-1)'
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Enhanced Description:</strong>
                      <div style={{
                        background: 'var(--background-color)',
                        padding: '1rem',
                        borderRadius: '8px',
                        margin: '0.5rem 0',
                        lineHeight: '1.6',
                        border: '1px solid var(--border-color-light)'
                      }}>
                        {tools.results.image_generation.result.description}
                      </div>
                    </div>
                  )}

                  {tools.results.image_generation.result.fallback && (
                    <div style={{
                      background: '#fff3cd',
                      border: '1px solid #ffeaa7',
                      color: '#856404',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      margin: '0.5rem 0',
                      fontSize: '0.875rem'
                    }}>
                      <strong>Note:</strong> {tools.results.image_generation.result.note}
                    </div>
                  )}

                  {tools.results.image_generation.timestamp && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                      Generated at: {new Date(tools.results.image_generation.timestamp).toLocaleString()}
                    </div>
                  )}
                </div>
              </ToolResultCard>
            )}

            {tools.results.url_context && (
              <ToolResultCard>
                <ToolResultHeader>
                  <FontAwesomeIcon icon={faLink} />
                  Web Content Analysis
                </ToolResultHeader>
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Analyzed URL:</strong>
                    <div style={{ margin: '0.5rem 0' }}>
                      <a href={tools.results.url_context.url} target="_blank" rel="noopener noreferrer" style={{
                        color: 'var(--primary-color)',
                        textDecoration: 'none',
                        padding: '0.5rem',
                        background: 'var(--background-color)',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color-light)',
                        display: 'inline-block'
                      }}>
                        {tools.results.url_context.url}
                      </a>
                      {tools.results.url_context.httpStatus && (
                        <span style={{
                          marginLeft: '0.5rem',
                          padding: '0.25rem 0.5rem',
                          background: tools.results.url_context.httpStatus === 200 ? '#d1fae5' : '#fee2e2',
                          color: tools.results.url_context.httpStatus === 200 ? '#065f46' : '#991b1b',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          {tools.results.url_context.httpStatus}
                        </span>
                      )}
                    </div>
                  </div>

                  {tools.results.url_context.metadata && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Page Metadata:</strong>
                      <div style={{
                        background: 'var(--background-color)',
                        padding: '0.75rem',
                        borderRadius: '4px',
                        margin: '0.5rem 0',
                        border: '1px solid var(--border-color-light)'
                      }}>
                        {tools.results.url_context.metadata.title && (
                          <div><strong>Title:</strong> {tools.results.url_context.metadata.title}</div>
                        )}
                        {tools.results.url_context.metadata.description && (
                          <div style={{ marginTop: '0.5rem' }}>
                            <strong>Description:</strong> {tools.results.url_context.metadata.description}
                          </div>
                        )}
                        {tools.results.url_context.wordCount && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                            Content: {tools.results.url_context.wordCount.toLocaleString()} words
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {tools.results.url_context.analysis && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>AI Analysis:</strong>
                      <div style={{
                        background: 'var(--background-color)',
                        padding: '1rem',
                        borderRadius: '8px',
                        margin: '0.5rem 0',
                        lineHeight: '1.6',
                        border: '1px solid var(--border-color-light)'
                      }}>
                        {tools.results.url_context.analysis}
                      </div>
                    </div>
                  )}

                  {tools.results.url_context.fetchTimestamp && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                      Analyzed at: {new Date(tools.results.url_context.fetchTimestamp).toLocaleString()}
                    </div>
                  )}
                </div>
              </ToolResultCard>
            )}
          </div>
        )}

        {tools.fileAnalyses && Object.keys(tools.fileAnalyses).length > 0 && (
          <div>
            <h4 style={{ color: 'var(--accent-color)', marginBottom: '0.5rem' }}>📄 Document Analysis:</h4>
            {Object.values(tools.fileAnalyses).map((analysis, index) => (
              <ToolResultCard key={index}>
                <ToolResultHeader>
                  <FontAwesomeIcon icon={faFile} />
                  {analysis.name}
                </ToolResultHeader>
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{
                      background: 'var(--background-color)',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border-color-light)'
                    }}>
                      <div><strong>Type:</strong> {analysis.type || 'Unknown'}</div>
                      <div><strong>Size:</strong> {analysis.size ? `${(analysis.size / 1024).toFixed(1)} KB` : 'Unknown'}</div>
                      {analysis.analysisType && (
                        <div><strong>Analysis Type:</strong> {analysis.analysisType}</div>
                      )}
                      {analysis.wordCount && (
                        <div><strong>Word Count:</strong> {analysis.wordCount.toLocaleString()}</div>
                      )}
                      {analysis.characterCount && (
                        <div><strong>Characters:</strong> {analysis.characterCount.toLocaleString()}</div>
                      )}
                    </div>
                  </div>

                  {analysis.warning && (
                    <div style={{
                      background: '#fff3cd',
                      border: '1px solid #ffeaa7',
                      color: '#856404',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      margin: '0.5rem 0',
                      fontSize: '0.875rem'
                    }}>
                      <strong>Warning:</strong> {analysis.warning}
                    </div>
                  )}

                  {analysis.error ? (
                    <div style={{
                      background: '#fee2e2',
                      border: '1px solid #fecaca',
                      color: '#991b1b',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      margin: '0.5rem 0'
                    }}>
                      <strong>Error:</strong> {analysis.analysis}
                    </div>
                  ) : (
                    <div>
                      <strong>Document Analysis:</strong>
                      <div style={{
                        background: 'var(--background-color)',
                        padding: '1rem',
                        borderRadius: '8px',
                        margin: '0.5rem 0',
                        lineHeight: '1.6',
                        border: '1px solid var(--border-color-light)'
                      }}>
                        {analysis.analysis}
                      </div>
                    </div>
                  )}

                  {analysis.timestamp && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                      Analyzed at: {new Date(analysis.timestamp).toLocaleString()}
                    </div>
                  )}
                </div>
              </ToolResultCard>
            ))}
          </div>
        )}
      </div>
    );
  };

  const getAgentColor = (agentClass) => {
    const colors = {
      supervisor: '#1976d2',           // Primary Blue
      strategist: '#7b1fa2',           // Warm Purple
      copywriter: '#ff6d00',           // Warm Orange
      seo_specialist: '#689f38',       // Warm Green
      ppc_manager: '#00acc1',          // Teal
      social_media_manager: '#f57c00',  // Amber
      data_analyst: '#c2185b',         // Warm Pink
      graphic_designer: '#546e7a',     // Warm Gray
      client_manager: '#00796b',       // Deep Teal
      user_input: '#558b2f',           // Olive Green
      default: '#546e7a'               // Warm Gray
    };
    return colors[agentClass] || colors.default;
  };

  const getIconForAgent = (iconName) => {
    const iconMap = {
      faUserCog: faUserCog,    // Supervisor
      faBrain: faBrain,        // Strategist
      faPencilAlt: faPencilAlt, // Copywriter
      faSearch: faSearch,      // SEO Specialist
      faBullseye: faBullseye,  // PPC Manager
      faHashtag: faHashtag,    // Social Media Manager
      faHandshake: faHandshake, // Client Manager
      faTrophy: faTrophy,      // Default
      faUser: faUser,          // User
      faRobot: faRobot         // AI/Assistant
    };
    return iconMap[iconName] || faRobot;
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          .loader {
            border: 4px solid #f3f3f3;
            border-top: 4px solid #3498db;
            border-radius: 50%;
            width: 30px;
            height: 30px;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .log-details {
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.5s ease-out;
          }
        `
      }} />
      <ChatContainer>
      <ChatHeader>
        <HeaderTitle>
          <CharacterIcon>
            <FontAwesomeIcon icon={getIconForAgent(currentCharacter?.icon) || faRobot} />
          </CharacterIcon>
          <CharacterInfo>
            <CharacterName>{currentCharacter?.name || 'AI Assistant'}</CharacterName>
            <CharacterRole>{currentCharacter?.role || 'Digital Assistant'}</CharacterRole>
          </CharacterInfo>
        </HeaderTitle>
        <HeaderActions>
          {isSupervisorMode && deliverables && deliverables.raw.length > 0 && (
            <ActionButton
              onClick={() => setShowFinalSummary(!showFinalSummary)}
              title="הצג סיכום תוצרים"
            >
              <FontAwesomeIcon icon={faClipboardList} />
              {deliverables.raw.length > 0 && (
                <span style={{
                  marginLeft: '0.25rem',
                  background: 'var(--primary-color)',
                  color: 'white',
                  borderRadius: '50%',
                  padding: '0.1rem 0.3rem',
                  fontSize: '0.7rem',
                  fontWeight: 'bold'
                }}>
                  {deliverables.raw.length}
                </span>
              )}
            </ActionButton>
          )}
          <ActionButton onClick={() => fileInputRef.current?.click()}>
            <FontAwesomeIcon icon={faPaperclip} />
          </ActionButton>
        </HeaderActions>
      </ChatHeader>

      <MessagesContainer ref={messagesContainerRef}>

        {/* Show Final Summary when project is completed */}
        {isSupervisorMode && showFinalSummary && deliverables && (
          <FinalSummaryView
            deliverables={deliverables}
            onActionExecute={async (actions, options) => {
              console.log('Executing actions:', actions, options);
              // Here you can implement actual action execution
              // For example, calling APIs to create campaigns, send emails, etc.

              // Show confirmation dialog
              if (options.requireConfirmation !== false) {
                const confirmed = window.confirm(
                  `האם לבצע ${actions.length} פעולות?\n` +
                  actions.map(a => `• ${a.label}`).join('\n')
                );
                if (!confirmed) return;
              }

              // Execute actions
              for (const action of actions) {
                switch (action.type) {
                  case 'copy_text':
                    navigator.clipboard.writeText(action.content || deliverables.raw.map(d => d.content).join('\n\n'));
                    alert('הטקסט הועתק ללוח!');
                    break;

                  case 'export_pdf':
                    // Implement PDF export
                    console.log('Exporting to PDF...');
                    break;

                  case 'create_google_campaign':
                    // Implement Google Ads campaign creation
                    console.log('Creating Google campaign...', action);
                    alert('הקמפיין מוכן לייצוא ל-Google Ads');
                    break;

                  case 'create_fb_campaign':
                    // Implement Facebook campaign creation
                    console.log('Creating Facebook campaign...', action);
                    alert('הקמפיין מוכן לייצוא ל-Facebook');
                    break;

                  default:
                    console.log('Unknown action:', action);
                }
              }
            }}
            onExport={(deliverables, format) => {
              console.log('Exporting deliverables:', format);
              // Implement export functionality
              switch (format) {
                case 'pdf':
                  alert('ייצוא ל-PDF בפיתוח...');
                  break;
                case 'share':
                  if (navigator.share) {
                    navigator.share({
                      title: 'תוצרי פרויקט',
                      text: deliverables.raw.map(d => `${d.agentName}:\n${d.content}`).join('\n\n'),
                    });
                  }
                  break;
              }
            }}
          />
        )}

        {/* Supervisor conversation */}
        {isSupervisorMode && supervisorState.conversationHistory && supervisorState.conversationHistory.length > 0 && supervisorState.conversationHistory.map((entry, index) => {
          const agent = characters.find(c => c.id === entry.agent);
          const agentColor = getAgentColor(agent?.agentClass || entry.type);
          
          if (entry.type === 'user_brief' || entry.type === 'user_response') {
            return (
              <MessageWrapper key={index} isUser={true}>
                <MessageBubble isUser={true}>
                  <MessageContent dangerouslySetInnerHTML={{ __html: formatContent(entry.content) }} />
                </MessageBubble>
              </MessageWrapper>
            );
          }

          // Special handling for system errors
          if (entry.type === 'system_error') {
            return (
              <div key={index} style={{
                background: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                padding: '1rem',
                margin: '0.5rem 0',
                color: '#991b1b'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  ⚠️ שגיאת מערכת
                </div>
                <MessageContent dangerouslySetInnerHTML={{ __html: formatContent(entry.content) }} />
              </div>
            );
          }

          // Special handling for ask_user questions - make them prominent
          if (entry.type === 'ask_user' || (entry.agent === 'supervisor' && entry.content.includes('**שאלה ללקוח'))) {
            return (
              <div key={index} style={{
                background: 'linear-gradient(135deg, #1976d2, #1565c0)',
                color: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                margin: '1rem 0',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                border: '2px solid #1976d2'
              }}>
                <div style={{ 
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>❓</span>
                  איתי כהן - מנהל הפרויקט שואל:
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '1rem',
                  fontSize: '1rem',
                  lineHeight: '1.6'
                }}>
                  <MessageContent dangerouslySetInnerHTML={{ __html: formatContent(entry.content) }} />
                </div>
                <div style={{
                  marginTop: '1rem',
                  fontSize: '0.9rem',
                  opacity: '0.9',
                  fontStyle: 'italic'
                }}>
                  💡 אנא ענה בתיבת הטקסט למטה
                </div>
              </div>
            );
          }
          
          return (
            <AgentCard key={index} borderColor={agentColor} className={entry.isThinking ? 'active' : ''}>
              <AgentHeader>
                <AgentIcon bgColor={agentColor}>
                  <FontAwesomeIcon icon={getIconForAgent(agent?.icon) || faRobot} />
                </AgentIcon>
                <AgentInfo>
                  <AgentName>{agent?.name || entry.agent}</AgentName>
                  <AgentRole>{agent?.role || ''}</AgentRole>
                </AgentInfo>
              </AgentHeader>
              {entry.isThinking ? (
                <ThinkingIndicator>
                  <div className="loader"></div>
                  <span>חושב...</span>
                </ThinkingIndicator>
              ) : (
                <MessageContent dangerouslySetInnerHTML={{ __html: formatContent(entry.content) }} />
              )}
              {entry.logData && <LogViewer logData={entry.logData} />}
            </AgentCard>
          );
        })}

        {/* Loading indicator for supervisor */}
        {isSupervisorMode && supervisorState.isRunning && !supervisorState.waitingForUser && (
          <LoadingIndicator>
            <div className="loader"></div>
            <span>חושבים על הצעד הבא...</span>
          </LoadingIndicator>
        )}

        {/* Regular chat messages (for non-supervisor characters) */}
        {console.log('[ChatWindowEnhanced] Render check - About to render messages:', {
          isSupervisorMode,
          hasConversation: !!currentConversation,
          conversationId: currentConversation?.id,
          messageCount: currentConversation?.messages?.length || 0,
          firstMessage: currentConversation?.messages?.[0],
          messages: currentConversation?.messages
        })}
        {currentConversation?.messages?.map((msg, index) => (
          <MessageWrapper key={index} isUser={msg.role === 'user'}>
            <MessageBubble isUser={msg.role === 'user'}>
              {msg.role === 'assistant' && (
                <MessageHeader>
                  <FontAwesomeIcon icon={getIconForAgent(currentCharacter?.icon) || faRobot} />
                  <span>{currentCharacter?.name || 'AI'}</span>
                </MessageHeader>
              )}
              <MessageContent dangerouslySetInnerHTML={{ __html: formatContent(msg.content) }} />

              {/* File Attachments in Messages */}
              {msg.attachments && msg.attachments.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'var(--space-3)',
                  marginTop: 'var(--space-3)'
                }}>
                  {msg.attachments.map((file, fileIndex) => (
                    <FileViewer
                      key={fileIndex}
                      file={file}
                      onDownload={async (fileData) => {
                        // Handle file download
                        console.log('Download file:', fileData);
                      }}
                    />
                  ))}
                </div>
              )}

              {msg.tools && renderToolResults(msg.tools)}
              {msg.tools?.thinkingUsed && (
                <div style={{
                  background: 'var(--surface-warm)',
                  border: '1px solid var(--accent-color)',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  margin: '0.5rem 0',
                  fontSize: '0.875rem',
                  color: 'var(--accent-color)'
                }}>
                  <strong>🧠 Advanced Reasoning Used:</strong> This response was generated using Gemini's thinking capabilities with {msg.tools.thinkingBudget} token budget for enhanced reasoning.
                </div>
              )}
            </MessageBubble>
          </MessageWrapper>
        ))}

        {/* Streaming message display */}
        {streamingMessage && !isSupervisorMode && (
          <MessageWrapper isUser={false}>
            <MessageBubble isUser={false}>
              <MessageHeader>
                <FontAwesomeIcon icon={getIconForAgent(currentCharacter?.icon) || faRobot} />
                <span>{currentCharacter?.name || 'AI'}</span>
              </MessageHeader>
              <MessageContent dangerouslySetInnerHTML={{ __html: formatContent(streamingMessage.content) }} />
              {!streamingMessage.done && (
                <div style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '16px',
                  background: 'var(--primary-600)',
                  marginLeft: '2px',
                  animation: 'blink 1s infinite'
                }} />
              )}
            </MessageBubble>
          </MessageWrapper>
        )}
        
        <div ref={messagesEndRef} />
      </MessagesContainer>

      {/* Scroll to bottom button - positioned outside MessagesContainer */}
      {showScrollButton && (
        <ScrollToBottomButton onClick={scrollToBottom}>
          <FontAwesomeIcon icon={faChevronDown} />
          <span>New messages</span>
        </ScrollToBottomButton>
      )}

      {/* File Attachments */}
      {attachedFiles.length > 0 && (
        <FileAttachments>
          {attachedFiles.map((file, index) => (
            <FileAttachment key={index}>
              <FontAwesomeIcon icon={file.type.startsWith('image/') ? faImage : faFile} />
              <span>{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
              <button
                onClick={() => removeAttachedFile(index)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  padding: '2px',
                  marginRight: '5px'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </FileAttachment>
          ))}
        </FileAttachments>
      )}

      <InputContainer>
        <InputWrapper>
          <FileButton
            onClick={() => {
              if (onFileUpload) {
                onFileUpload();
              } else {
                setIsFileUploadOpen(true);
              }
            }}
            title="Attach files"
            disabled={isLoading || streamingMessage}
          >
            <FontAwesomeIcon icon={faPaperclip} />
          </FileButton>
          <MessageInput
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!streamingMessage) handleSendMessage();
              }
            }}
            placeholder={
              isSupervisorMode && supervisorState.waitingForUser
                ? "הקלד את תשובתך לשאלה של איתי..."
                : isSupervisorMode && !supervisorState.isRunning
                ? "תאר את הפרויקט שלך והמטרות שלך..."
                : "הקלד הודעה..."
            }
            disabled={isLoading || streamingMessage}
          />
        </InputWrapper>
        <SendButton onClick={handleSendMessage} disabled={isLoading || !message.trim() || streamingMessage} hasText={!!message.trim()}>
          <FontAwesomeIcon icon={faPaperPlane} />
        </SendButton>
      </InputContainer>
      
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        multiple
        accept="image/*,application/pdf,.doc,.docx,.txt"
        onChange={handleFileUpload}
      />
    </ChatContainer>

    {/* File Upload Modal */}
    {isFileUploadOpen && (
      <FileUploadModal
        isOpen={isFileUploadOpen}
        onClose={() => setIsFileUploadOpen(false)}
        userId={userId}
        conversationId={currentConversation?.id}
        onUploadComplete={(files) => {
          console.log('Files uploaded:', files);
          // Handle successful upload - maybe show in chat or update UI
          // You can add logic here to show files in the chat
        }}
      />
    )}
    </>
  );
}

export default ChatWindowEnhanced;
